import { defineStore } from "pinia";
import type { WebModelGetAppVersion } from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import {
  findApps,
  findAppsPage,
  getAppById,
  getAppWithHistory,
  getIdOfLatestReleaseByAppIdentifier,
} from "~/model/api/pLCnextStoreFrontendAPI";
import { withErrorMessage } from "~/helpers/withErrorMessage";
import { withErrorHandling } from "~/helpers/withErrorHandling";
import { withLoading } from "~/helpers/withLoading";
import type { FilterState } from "~/stores/ui/specific/appsfilterstore";
import { submitSearchEventToGoogleAnalytics } from "~/helpers/google-analytics";

interface State {
  /**
   * Cache of app versions.
   * Key includes appId + filter flags so different "views" (archived/deactivated) don't overwrite each other.
   */
  appVersions: Record<string, WebModelGetAppVersion[]>;
}

function makeCacheKey(
  appId: number,
  archived: boolean,
  deactivated: boolean,
  drafted: boolean,
): string {
  return `${appId}|archived=${archived ? 1 : 0}|deactivated=${deactivated ? 1 : 0}|drafted=${drafted ? 1 : 0}`;
}

function makeAppVersionIdCacheKey(appVersionId: number): string {
  return `appVersionId=${appVersionId}`;
}

export const useAppVersionsStore = defineStore("appVersions", {
  state: (): State => ({
    appVersions: {},
  }),

  getters: {
    /**
     * Convenient getter if you want cached data without triggering a network call.
     * Can be used in computed properties
     */
    getCachedAppVersionById:
      (state) =>
      (appVersionId: number): WebModelGetAppVersion | null => {
        const key = makeAppVersionIdCacheKey(appVersionId);
        const cached = state.appVersions[key];
        // Extract the first element from the array, or return null if not found
        return cached?.[0] ?? null;
      },
    /**
     * Convenient getter if you want cached data without triggering a network call.
     * Can be used in computed properties
     */
    getCachedAppVersions:
      (state) =>
      (
        appId: number,
        archived: boolean = false,
        deactivated: boolean = false,
        drafted: boolean = false,
      ) => {
        const key = makeCacheKey(appId, archived, deactivated, drafted);
        return state.appVersions[key];
      },
    getLastestAppVersionForAppId:
      (state) =>
      (
        appId: number,
        archived: boolean = false,
        deactivated: boolean = false,
        drafted: boolean = false,
      ): WebModelGetAppVersion | null => {
        const key = makeCacheKey(appId, archived, deactivated, drafted);
        const appVersions = state.appVersions[key];

        if (!appVersions || appVersions.length === 0) {
          return null;
        }

        return appVersions.reduce((prev, current) => {
          const prevV = prev.internalVersion ?? Number.NEGATIVE_INFINITY;
          const curV = current.internalVersion ?? Number.NEGATIVE_INFINITY;
          return prevV > curV ? prev : current;
        });
      },

    getSortOptions: () => {
      return [
        {
          text: "Newest",
          value: "-time",
        },
        {
          text: "Most Popular",
          value: "-downloads",
        },
        {
          text: "Price low to high",
          value: "price",
        },
        {
          text: "Alphabetical A->Z",
          value: "name",
        },
      ];
    },
  },

  actions: {
    /**
     * Looks up the cache first and only makes a network call if needed.
     * - If cached, returns immediately.
     * - If not cached (or `forceRefresh=true`), fetches and caches.
     */
    async getOrLoadAppVersions(
      appId: number,
      archived: boolean = false,
      deactivated: boolean = false,
      drafted: boolean = false,
      forceRefresh: boolean = false,
    ): Promise<WebModelGetAppVersion[] | undefined> {
      if (!forceRefresh) {
        const cached = this.getCachedAppVersions(appId, archived, deactivated);
        if (cached) return cached;
      }
      return await this.loadAppVersions(
        appId,
        archived,
        deactivated,
        drafted,
        forceRefresh,
      );
    },

    /**
     * Find AppVersion by its id:
     * 1) Try cached (across all cache keys)
     * 2) If not found (or forceRefresh), load it from backend and cache it.
     */
    async getOrLoadAppVersionById(
      appVersionId: number,
      forceRefresh: boolean = false,
    ): Promise<WebModelGetAppVersion | undefined> {
      if (!appVersionId) {
        console.warn("getOrLoadAppVersionById: appVersionId is undefined");
        return undefined;
      }
      const idKey = makeAppVersionIdCacheKey(appVersionId);

      if (!forceRefresh) {
        // Check the dedicated by-id key first
        if (this.appVersions[idKey]?.[0]) {
          return this.appVersions[idKey][0];
        }
        // Fall back to scanning other cache entries
        for (const list of Object.values(this.appVersions)) {
          const hit = list?.find((v) => v.id === appVersionId);
          if (hit) {
            // Always populate the dedicated key so computed properties can track it
            this.appVersions[idKey] = [hit];
            return hit;
          }
        }
      }
      console.debug("getOrLoadAppVersionById:", { appVersionId }, forceRefresh);

      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(getAppById(appVersionId))),
      );

      console.debug("getOrLoadAppVersionById:", { appVersionId }, data, error);

      if (!data) {
        console.debug("getOrLoadAppVersionById: no data for id", appVersionId);
        return undefined;
      }

      // cache under a stable “by id” key
      this.appVersions[makeAppVersionIdCacheKey(appVersionId)] = [data];

      // (optional) also merge into any existing appId-based lists, if present
      const appId = data.appId;
      if (typeof appId === "number") {
        for (const [key, list] of Object.entries(this.appVersions)) {
          if (!key.startsWith(`${appId}|`)) continue;
          if (!Array.isArray(list)) continue;

          const idx = list.findIndex((v) => v.id === appVersionId);
          if (idx >= 0) list[idx] = data;
          else list.push(data);
        }
      }

      return data;
    },

    async loadAppVersionsByCompanyIdAndHub(companyId: string, hubName: string) {
      console.debug("loadAppVersionsByCompanyId: ", companyId);
      const { data, error } = await withErrorHandling(
        withErrorMessage(
          withLoading(
            findApps({ company_ids: [companyId], hubName, status: [1] }),
          ),
        ),
      );
      console.debug("loadAppVersionsByCompanyId: ", companyId, data, error);
      if (data) {
        // Cache each app version by its id
        for (const appVersion of data) {
          if (appVersion.id != null) {
            this.appVersions[makeAppVersionIdCacheKey(appVersion.id)] = [
              appVersion,
            ];
          }
        }
        return data;
      }
      return [];
    },

    async loadPagedAppVersionsWithParams(
      hubName: string,
      filterState: FilterState,
    ) {
      console.debug("loadPagedAppVersionsWithParams: ", hubName, filterState);
      const { data, error } = await withErrorHandling(
        withErrorMessage(
          withLoading(
            findAppsPage({
              hubName,
              pageSize: filterState.pageSize,
              pageNumber: filterState.page === null ? 0 : filterState.page,
              name: filterState.name ? filterState.name : undefined,
              status: [1],
              sort: filterState.sort ? filterState.sort : undefined,
              free_apps: filterState.free_apps
                ? filterState.free_apps
                : undefined,
              category_ids: filterState.category_ids
                ? filterState.category_ids
                : [],
              type: filterState.type ? filterState.type : undefined,
              company_ids: filterState.company_ids ?? undefined,
              hardware: filterState.hardware
                ? [filterState.hardware]
                : undefined,
            }),
          ),
        ),
      );
      console.debug(
        "loaded pagedAppVersionsWithParams ",
        hubName,
        filterState,
        data,
        error,
      );

      // only add to google analytics if the search term is not empty and the page is the first page.
      if (filterState.name && (!filterState.page || filterState.page < 2)) {
        const filterStateAsArray = Object.entries(filterState).map(
          ([key, value]) => ({
            [key]: value,
          }),
        );
        submitSearchEventToGoogleAnalytics(
          filterState.name,
          data?.totalElements || 0,
          filterStateAsArray,
        );
      }

      if (data?.content) {
        // Cache each app version by its id
        for (const appVersion of data.content) {
          if (appVersion.id != null) {
            this.appVersions[makeAppVersionIdCacheKey(appVersion.id)] = [
              appVersion,
            ];
          }
        }
        return data;
      }
      return {
        content: [],
        totalElements: 0,
      };
    },

    async getAppVersionIdByProductcodeAndFirmcode(appIdentifier: string) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(
          withLoading(getIdOfLatestReleaseByAppIdentifier(appIdentifier)),
        ),
      );
      console.debug(
        "getAppVersionByProductcodeAndFirmcode: ",
        { appIdentifier },
        data,
        error,
      );
      if (data) {
        return data;
      }
      return undefined;
    },

    /* PRIVATE BELOW */
    /**
     * Loads versions and caches them.
     * - If cached and `forceRefresh=false`, returns cached value and skips API.
     * - Cache key includes (appId, archived, deactivated).
     */
    async loadAppVersions(
      appId: number,
      archived: boolean = false,
      deactivated: boolean = false,
      drafted: boolean = false,
      forceRefresh: boolean = false,
    ): Promise<WebModelGetAppVersion[] | undefined> {
      const key = makeCacheKey(appId, archived, deactivated, drafted);

      if (!forceRefresh && this.appVersions[key]) {
        return this.appVersions[key];
      }

      const { data, error } = await withErrorHandling(
        withErrorMessage(
          withLoading(
            getAppWithHistory(appId, { archived, deactivated, drafted }),
          ),
        ),
      );

      console.debug(
        "loadAppVersions: ",
        { appId, archived, deactivated },
        data,
        error,
      );

      if (data) {
        // keep already-cached entries for other appIds/flag-combos intact
        this.appVersions[key] = data;
        return data;
      }

      return undefined;
    },
    /**
     * Optional: clear only one cached entry instead of the whole store.
     */
    clearCacheEntry(
      appId: number,
      archived: boolean = false,
      deactivated: boolean = false,
      drafted: boolean = false,
    ) {
      const key = makeCacheKey(appId, archived, deactivated, drafted);
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this.appVersions[key];
    },

    clearAppVersionCacheEntry(appVersionId: number) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this.appVersions[makeAppVersionIdCacheKey(appVersionId)];

      // Also remove the deleted version from any cached app version lists
      for (const [_unusedKey, list] of Object.entries(this.appVersions)) {
        if (!Array.isArray(list)) continue;
        const index = list.findIndex((v) => v.id === appVersionId);
        if (index >= 0) {
          list.splice(index, 1);
        }
      }
    },

    // easily reset state using `$reset`
    clear() {
      this.$reset();
    },
  },
});
